import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SidebarLayout from "../components/sidebarLayout";
import {
  createUsageType,
  deleteUsageType,
  getAllUsageTypes,
  updateUsageType,
} from "../APIs/UsageTypesAPI";
import { useSnackbar } from "../contexts/SnackBarContext";
import { useTranslation } from "react-i18next";
import { localizedErrorMessage } from "../utils/italianErrorMessage";

const EMPTY_FORM = {
  name: "",
  description: "",
  active: true,
};

export default function GestioneUsageTypesPage() {
  const token = localStorage.getItem("accessToken");
  const tenant = localStorage.getItem("tenantId");
  const tenantType = String(localStorage.getItem("tenantType") || "").toUpperCase();
  const { showSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getAllUsageTypes(token, tenant);
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(localizedErrorMessage(err, t("usageTypes.loadError")));
    } finally {
      setLoading(false);
    }
  }, [tenant, token, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      description: item.description || "",
      active: item.active !== false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showSnackbar(t("usageTypes.validation.nameRequired"), "warning");
      return;
    }
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || null,
        active: Boolean(form.active),
      };
      if (editing?.id) {
        await updateUsageType(editing.id, payload, token, tenant);
        showSnackbar(t("usageTypes.updated"), "success");
      } else {
        await createUsageType(payload, token, tenant);
        showSnackbar(t("usageTypes.created"), "success");
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      showSnackbar(err?.message || t("usageTypes.saveError"), "error");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(t("usageTypes.deleteConfirm", { name: item.name }))) return;
    try {
      await deleteUsageType(item.id, token, tenant);
      showSnackbar(t("usageTypes.deleted"), "success");
      await loadData();
    } catch (err) {
      showSnackbar(err?.message || t("usageTypes.deleteError"), "error");
    }
  };

  if (tenantType !== "SALE_CONGRESSI") {
    return (
      <SidebarLayout className="admin-responsive-page">
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Alert severity="info">{t("usageTypes.conferenceOnly")}</Alert>
        </Box>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout className="admin-responsive-page">
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight={900}>{t("usageTypes.title")}</Typography>
            <Typography color="text.secondary">
              {t("usageTypes.subtitle")}
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            {t("usageTypes.newType")}
          </Button>
        </Stack>

        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Grid container spacing={2}>
          {items.map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item.id}>
              <Card sx={{ borderRadius: 3, height: "100%", border: "1px solid", borderColor: "divider" }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box minWidth={0}>
                      <Typography variant="h6" fontWeight={900} noWrap>{item.name}</Typography>
                      <Chip
                        size="small"
                        sx={{ mt: 1 }}
                        color={item.active ? "success" : "default"}
                        label={item.active ? t("common.status.active") : t("common.status.inactive")}
                      />
                    </Box>
                    <Stack direction="row">
                      <IconButton onClick={() => openEdit(item)}><EditIcon /></IconButton>
                      <IconButton color="error" onClick={() => handleDelete(item)}><DeleteIcon /></IconButton>
                    </Stack>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, minHeight: 42 }}>
                    {item.description || t("usageTypes.noDescription")}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {!loading && items.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="info">{t("usageTypes.empty")}</Alert>
            </Grid>
          ) : null}
        </Grid>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{editing ? t("usageTypes.editType") : t("usageTypes.newUsageType")}</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label={t("usageTypes.name")}
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              />
              <TextField
                fullWidth
                multiline
                minRows={3}
                label={t("usageTypes.description")}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              />
              <Stack direction="row" alignItems="center" spacing={1}>
                <Switch
                  checked={form.active}
                  onChange={(e) => setForm((prev) => ({ ...prev, active: e.target.checked }))}
                />
                <Typography fontWeight={800}>{t("usageTypes.typeActive")}</Typography>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>{t("common.actions.cancel")}</Button>
            <Button variant="contained" onClick={handleSave}>{t("common.actions.save")}</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SidebarLayout>
  );
}
